import ContentEditable from "react-contenteditable";
import style from "./Text.module.css";
import { ref, update } from "firebase/database";
import { db } from "../../../configs/Firebase";
import sanitizeHTML from "sanitize-html";
import { useEffect, useState, useRef } from "react";
import Icon from "../../../designSystem/a-atoms/icons/Icon";
import StyleSwatch from "../parts/StyleSwatch";

const sanitizeOptions = {
  allowedTags: ['b', 'i', 'em', 'strong', 'u', 'p', 'br', 'ul', 'ol', 'li', 'div'],
  allowedAttributes: { div: ['style'], p: ['style'] },
  allowedSchemes: [],
};

const Text = (props) => {
  const [hasRendered, setHasRendered] = useState(false);
  const [cellVal, setCellVal] = useState("");
  const [color, setColor] = useState("style1");
  const [textAlign, setTextAlign] = useState("left");
  const [showToolbar, setShowToolbar] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ left: null, right: null });
  const cellRef = useRef(null);
  const toolbarRef = useRef(null);
  const toolbarTimeoutRef = useRef(null);

  useEffect(() => {
    if(props.data.style) {
      setColor(props.data.style);
    }
  },[props.data.style])

  useEffect(() => {
    if(props.data.textAlign) {
      setTextAlign(props.data.textAlign);
    }
  },[props.data.textAlign])

  useEffect(() => {
    if (showToolbar && cellRef.current) {
      // Use setTimeout to ensure toolbar is rendered before measuring
      const timeoutId = setTimeout(() => {
        if (!toolbarRef.current) return;

        // Find the journey container (.map)
        let container = cellRef.current.closest('.map');
        if (!container) {
          // Fallback: find any scrollable parent
          container = cellRef.current.closest('[class*="journey"], [class*="map"]') || document.body;
        }

        const cellRect = cellRef.current.getBoundingClientRect();
        const toolbarRect = toolbarRef.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Get padding value
        const paddingValue = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--padding--small') || '8');

        // Calculate if toolbar would overflow on the right
        const toolbarRightEdge = cellRect.left + toolbarRect.width + paddingValue;
        const containerRightEdge = containerRect.right;

        if (toolbarRightEdge > containerRightEdge) {
          // Toolbar would overflow, position it from the right edge of the cell
          // Use the same padding value as the left positioning
          setToolbarPosition({ left: null, right: 'var(--padding--small)' });
        } else {
          // Toolbar fits, use default left positioning
          setToolbarPosition({ left: 'var(--padding--small)', right: null });
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    } else {
      // Reset position when toolbar is hidden
      setToolbarPosition({ left: null, right: null });
    }
  }, [showToolbar]);

  useEffect(() => {
    // if (!props.editable || !hasRendered) {
      // Preserve the value as-is, don't strip empty tags or line breaks
      setCellVal(props.data.value || "");
    // }
    setHasRendered(true);
  }, [props.data.value, props.editable]);

  function onChangeHandler(e, columnId) {
    const sanitized = sanitizeHTML(e.target.value, sanitizeOptions);
    update(ref(db), {
      [props.journeyRefURL +
      "/rows/" +
      props.row.id +
      "/columns/" +
      columnId +
      "/value"]: sanitized,
    });
    setCellVal(e.target.value);
  }

  // Handle paste events to strip HTML and only allow plain text
  // Users can format using the toolbar if needed
  function handlePaste(e) {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  }

  function selectAll(e) {
    var range, selection;
    if (document.body.createTextRange) {
      range = document.body.createTextRange();
      range.moveToElementText(e.currentTarget);
      range.select();
    } else if (window.getSelection) {
      selection = window.getSelection();
      range = document.createRange();
      range.selectNodeContents(e.currentTarget);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  function handleFocus(e) {
    setHasFocus(true);
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
  }

  function handleBlur(e) {
    setHasFocus(false);
  }

  function handleMouseEnter() {
    if (!props.editable) return;
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    setShowToolbar(true);
  }

  function handleMouseLeave(e) {
    if (!props.editable) return;
    // If moving into an element inside this cell (e.g. the toolbar), do not hide
    if (cellRef.current && e.relatedTarget && e.relatedTarget instanceof Node && cellRef.current.contains(e.relatedTarget)) {
      return;
    }
    setShowToolbar(false);
  }

  function formatBold(e) {
    e.preventDefault();
    e.stopPropagation();
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    const editableElement = cellRef.current?.querySelector('[contenteditable="true"]');
    if (editableElement) {
      editableElement.focus();
      document.execCommand('bold', false, null);
    }
  }

  function formatItalic(e) {
    e.preventDefault();
    e.stopPropagation();
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    const editableElement = cellRef.current?.querySelector('[contenteditable="true"]');
    if (editableElement) {
      editableElement.focus();
      document.execCommand('italic', false, null);
    }
  }

  function formatBulletList(e) {
    e.preventDefault();
    e.stopPropagation();
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    const editableElement = cellRef.current?.querySelector('[contenteditable="true"]');
    if (editableElement) {
      editableElement.focus();
      document.execCommand('insertUnorderedList', false, null);
    }
  }

  function formatNumberedList(e) {
    e.preventDefault();
    e.stopPropagation();
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    const editableElement = cellRef.current?.querySelector('[contenteditable="true"]');
    if (editableElement) {
      editableElement.focus();
      document.execCommand('insertOrderedList', false, null);
    }
  }

  function changeStyle(styleValue) {
    update(ref(db), {
      [props.journeyRefURL +
      "/rows/" +
      props.row.id +
      "/columns/" +
      props.cellIndex +
      "/style"]: styleValue,
    });
  }

  function formatAlignment(e, alignment) {
    e.preventDefault();
    e.stopPropagation();
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    const editableElement = cellRef.current?.querySelector('[contenteditable="true"]');
    if (editableElement) {
      editableElement.focus();
      let command = 'justifyLeft';
      if (alignment === 'center') {
        command = 'justifyCenter';
      } else if (alignment === 'right') {
        command = 'justifyRight';
      }
      document.execCommand(command, false, null);
      
      // Update Firebase
      update(ref(db), {
        [props.journeyRefURL +
        "/rows/" +
        props.row.id +
        "/columns/" +
        props.cellIndex +
        "/textAlign"]: alignment,
      });
      setTextAlign(alignment);
    }
  }

  const textStyleOptions = [
    { id: 'style1', label: 'Style 1', backgroundColor: '#fff', textColor: 'var(--on--200)' },
    { id: 'style2', label: 'Style 2', backgroundColor: '#4ea448', textColor: '#fff' },
    { id: 'style3', label: 'Style 3', backgroundColor: '#d89f00', textColor: '#fff' },
    { id: 'style4', label: 'Style 4', backgroundColor: '#d4452f', textColor: '#fff' },
    { id: 'style5', label: 'Style 5', backgroundColor: '#637fc5', textColor: '#fff' },
    { id: 'style6', label: 'Style 6', backgroundColor: 'transparent', textColor: 'var(--on--200)' },
  ];

  useEffect(() => {
    return () => {
      if (toolbarTimeoutRef.current) {
        clearTimeout(toolbarTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={cellRef}
      className={`${style.cell} ${style[color]} ${
        props.editable ? style.editable : null
      } ${(!cellVal || cellVal === "") && style.noValue}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {props.editable ? (
          <ContentEditable
            html={cellVal}
            onChange={(e) => onChangeHandler(e, props.cellIndex)}
            onPaste={handlePaste}
            inputMode="text"
          onFocus={(e) => {
            handleFocus(e);
            // selectAll(e);
          }}
          onBlur={handleBlur}
          style={{ textAlign: textAlign }}
        />
      ) : (
        <div
          style={{ textAlign: textAlign }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHTML(cellVal, sanitizeOptions),
          }}
        />
      )}
      {props.editable && showToolbar ? (
        <div 
          ref={toolbarRef}
          className={style.toolbar}
          style={toolbarPosition.left !== null 
            ? { left: toolbarPosition.left, right: 'auto' }
            : { left: 'auto', right: toolbarPosition.right }
          }
        >
          <button
            type="button"
            className={style.toolbarButton}
            disabled={!hasFocus}
            onClick={formatBold}
            onMouseDown={(e) => e.preventDefault()}
            title="Bold"
          >
            <Icon>format_bold</Icon>
          </button>
          <button
            type="button"
            className={style.toolbarButton}
            disabled={!hasFocus}
            onClick={formatItalic}
            onMouseDown={(e) => e.preventDefault()}
            title="Italic"
          >
            <Icon>format_italic</Icon>
          </button>
          <button
            type="button"
            className={style.toolbarButton}
            disabled={!hasFocus}
            onClick={formatBulletList}
            onMouseDown={(e) => e.preventDefault()}
            title="Bullet List"
          >
            <Icon>format_list_bulleted</Icon>
          </button>
          <button
            type="button"
            className={style.toolbarButton}
            disabled={!hasFocus}
            onClick={formatNumberedList}
            onMouseDown={(e) => e.preventDefault()}
            title="Numbered List"
          >
            <Icon>format_list_numbered</Icon>
          </button>
         
          <div className={style.toolbarSeparator}></div>
          <button
            type="button"
            className={`${style.toolbarButton} ${style.alignmentButton} ${textAlign === 'left' ? style.active : ''}`}
            disabled={!hasFocus}
            onClick={(e) => formatAlignment(e, 'left')}
            onMouseDown={(e) => e.preventDefault()}
            title="Align Left"
          >
            <Icon>format_align_left</Icon>
          </button>
          <button
            type="button"
            className={`${style.toolbarButton} ${style.alignmentButton} ${textAlign === 'center' ? style.active : ''}`}
            disabled={!hasFocus}
            onClick={(e) => formatAlignment(e, 'center')}
            onMouseDown={(e) => e.preventDefault()}
            title="Align Center"
          >
            <Icon>format_align_center</Icon>
          </button>
          <button
            type="button"
            className={`${style.toolbarButton} ${style.alignmentButton} ${textAlign === 'right' ? style.active : ''}`}
            disabled={!hasFocus}
            onClick={(e) => formatAlignment(e, 'right')}
            onMouseDown={(e) => e.preventDefault()}
            title="Align Right"
          >
            <Icon>format_align_right</Icon>
          </button>
          <div className={style.toolbarSeparator}></div>
          <StyleSwatch
            currentStyle={color}
            styleOptions={textStyleOptions}
            onStyleChange={changeStyle}
            variant="text"
          />
          {props.onDelete ? (
            <>
              <div className={style.toolbarSeparator}></div>
              <button
                type="button"
                className={`${style.toolbarButton} ${style.deleteButton}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (toolbarTimeoutRef.current) {
                    clearTimeout(toolbarTimeoutRef.current);
                  }
                  props.onDelete();
                }}
                onMouseDown={(e) => e.preventDefault()}
                title="Delete cell"
              >
                <Icon variants={["inheritColor"]}>close</Icon>
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default Text;
